<script setup lang="ts">
  /**
   * Radio — 03-components/20a-radio · OSCloud DS 1.1.34
   */
  import { useId } from 'vue'

  type RadioValue = string | number

  type RadioOption = {
    label: string
    value: RadioValue
    disabled?: boolean
  }

  const props = withDefaults(
    defineProps<{
      modelValue?: RadioValue
      /** The group's legend. The group is the field: one legend, one message. */
      label?: string
      options?: RadioOption[]
      /** Lays the options out in a row instead of a column. */
      inline?: boolean
      /** Neutral message under the group. */
      hint?: string
      /** Validation message; marks every input in the group invalid. */
      error?: string
      disabled?: boolean
      fullWidth?: boolean
      /** Shared input name; generated when omitted. */
      name?: string
    }>(),
    {
      modelValue: '',
      label: '',
      options: () => [],
      inline: false,
      hint: '',
      error: '',
      disabled: false,
      fullWidth: false,
      name: undefined,
    },
  )

  const emit = defineEmits<{ 'update:modelValue': [value: RadioValue] }>()

  const generatedName = useId()
  const groupName = props.name ?? generatedName
</script>

<template>
  <fieldset class="form-field" :class="{ 'form-field--full-width': fullWidth }">
    <legend v-if="label" class="form-field__label">{{ label }}</legend>
    <div class="form-field__control radio-group" :class="{ 'radio-group--inline': inline }">
      <label v-for="option in options" :key="String(option.value)" class="radio">
        <input
          type="radio"
          :name="groupName"
          :value="option.value"
          :checked="option.value === modelValue"
          :disabled="disabled || option.disabled"
          :aria-invalid="error ? 'true' : 'false'"
          @change="emit('update:modelValue', option.value)"
        />
        <span class="radio__dot" />
        <span class="form-field__label form-field__label--plain">{{ option.label }}</span>
      </label>
    </div>
    <span
      v-if="error || hint"
      class="form-field__message"
      :role="error ? 'alert' : undefined"
      :data-test-id="error ? 'field-error' : undefined"
    >
      {{ error || hint }}
    </span>
  </fieldset>
</template>

<style scoped lang="scss">
  /* A field rendered as <fieldset> sheds the UA chrome. */
  fieldset.form-field {
    margin: 0;
    padding: 0;
    border: 0;
  }

  legend.form-field__label {
    padding: 0;
  }

  .form-field {
    display: flex;
    flex-direction: column;
    min-width: 0;

    &--full-width {
      width: 100%;
    }

    &__label {
      font-family: var(--bf-font-display);
      font-size: 11px;
      font-weight: var(--bf-fw-bold);
      color: var(--bf-off-black);
      text-transform: uppercase;
      letter-spacing: var(--bf-tr-eyebrow);
    }

    > .form-field__label {
      margin-bottom: var(--bf-form-label-gap);
    }

    &__label--plain {
      font-size: var(--bf-fs-xs);
      font-weight: var(--bf-fw-semibold);
      line-height: var(--bf-lh-normal);
      color: var(--bf-fg-1);
      text-transform: none;
      letter-spacing: var(--bf-tr-normal);
    }

    &__control {
      position: relative;
    }

    &__message {
      color: var(--bf-fg-2);
      font-size: var(--bf-fs-xs);
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: var(--bf-form-message-gap);
    }

    &__message[role='alert'],
    &:has([aria-invalid='true']) &__message {
      color: var(--bf-form-invalid-text);
    }
  }

  .radio-group {
    display: flex;
    flex-direction: column;
    gap: var(--bf-sp-2);

    &--inline {
      flex-direction: row;
      flex-wrap: wrap;
      gap: var(--bf-sp-3);
    }
  }

  .radio {
    display: inline-flex;
    align-items: flex-start;
    gap: var(--bf-sp-2);
    cursor: pointer;
    user-select: none;

    input {
      position: absolute;
      opacity: 0;
      pointer-events: none;
    }

    /* The control carries the emphasis, so the option's own text reads as plain copy. */
    .form-field__label--plain {
      font-weight: var(--bf-fw-regular);
    }

    /* 16px dot, 1.5px hairline; checked, the border thickens to a 5px ring and
       the surface shows through as the centre. */
    &__dot {
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      margin-top: 1px;
      border: 1.5px solid var(--bf-dusky-2);
      border-radius: 50%;
      background: var(--bf-form-control-surface);
      transition:
        border-color var(--bf-dur-fast) var(--bf-ease),
        border-width var(--bf-dur-fast) var(--bf-ease),
        box-shadow var(--bf-dur-fast) var(--bf-ease);
    }

    input:checked + &__dot {
      border-width: 5px;
      border-color: var(--bf-orange-dark);
    }

    input:focus-visible + &__dot {
      border-color: var(--bf-form-control-hairline-focus);
      box-shadow: var(--bf-form-focus-ring);
    }

    &:has(input[aria-invalid='true']) &__dot {
      border-color: var(--bf-form-invalid-hairline);
    }

    &:has(input:disabled) {
      opacity: var(--bf-form-disabled-opacity);
      cursor: not-allowed;
    }
  }
</style>
